import React from 'react';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '@/components/ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { User, X, Search } from 'lucide-react';
import { toast } from 'sonner';
import { agencyAPI, agentAPI } from '@/lib/api';
import { useAuth } from '@/context/AuthContext';
import { isKRRole, isAgencyPrincipal, getErrorMessage, formatDate, formatPhoneInput, US_STATES } from '@/lib/utils';

/**
 * PrincipalPicker — searchable linker that binds an existing agent (by NPN) as
 * the agency's Principal. Loads the simple agent list once and filters client
 * side; stores the selected NPN + display name back on the form.
 */
const PrincipalPicker = ({ npn, name, onPick, onClear }) => {
  const [agents, setAgents] = React.useState([]);
  const [q, setQ] = React.useState('');
  const [open, setOpen] = React.useState(false);

  React.useEffect(() => {
    agentAPI.simpleList().then((r) => setAgents(r.data || [])).catch(() => setAgents([]));
  }, []);

  const matches = React.useMemo(() => {
    const t = q.trim().toLowerCase();
    if (!t) return [];
    return agents
      .filter((a) => `${a.name} ${a.npn}`.toLowerCase().includes(t))
      .slice(0, 8);
  }, [q, agents]);

  if (npn) {
    return (
      <div className="flex items-center gap-2 rounded-md border border-slate-200 bg-slate-50 px-3 py-2" data-testid="agency-principal-selected">
        <User size={14} className="text-slate-500" />
        <span className="text-sm font-medium text-slate-800">{name || `NPN ${npn}`}</span>
        <span className="text-xs text-slate-400 font-mono">NPN {npn}</span>
        <button type="button" onClick={onClear} className="ml-auto text-slate-400 hover:text-rose-500" data-testid="agency-principal-clear">
          <X size={14} />
        </button>
      </div>
    );
  }

  return (
    <div className="relative">
      <div className="relative">
        <Search size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400" />
        <Input
          value={q}
          onChange={(e) => { setQ(e.target.value); setOpen(true); }}
          onFocus={() => setOpen(true)}
          placeholder="Search an agent by name or NPN…"
          className="pl-8"
          data-testid="agency-principal-search"
        />
      </div>
      {open && matches.length > 0 && (
        <div className="absolute z-50 mt-1 w-full rounded-md border border-slate-200 bg-white shadow-lg max-h-56 overflow-y-auto" data-testid="agency-principal-results">
          {matches.map((a, idx) => (
            <button
              key={`${a.npn}-${idx}`}
              type="button"
              onClick={() => { onPick(a.npn, a.name); setQ(''); setOpen(false); }}
              className="flex w-full items-center gap-2 px-3 py-2 text-left text-sm hover:bg-slate-50"
              data-testid={`agency-principal-option-${a.npn}`}
            >
              <User size={13} className="text-slate-400" />
              <span className="font-medium text-slate-800">{a.name}</span>
              <span className="ml-auto text-xs text-slate-400 font-mono">{a.npn}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

const POLICY_LABELS = {
  direct_contact_allowed: 'Direct Contact Allowed',
  agency_coordination_required: 'Agency Coordination Required',
  restricted_direct_contact: 'Restricted Direct Contact',
};
const POLICY_DESCRIPTIONS = {
  direct_contact_allowed:
    'KR Enterprise Group may communicate directly with agents under this agency.',
  agency_coordination_required:
    'Communication with agents should normally be coordinated through the agency.',
  restricted_direct_contact:
    'Direct communication requires authorization.',
};
const POLICY_TONE = {
  direct_contact_allowed: 'bg-emerald-100 text-emerald-700 border-emerald-200',
  agency_coordination_required: 'bg-amber-100 text-amber-700 border-amber-200',
  restricted_direct_contact: 'bg-rose-100 text-rose-700 border-rose-200',
};

const CommunicationPolicySection = ({ agencyId }) => {
  const { user } = useAuth();
  const canManage = isKRRole(user);
  const canRequest = isAgencyPrincipal(user);
  const [policyState, setPolicyState] = React.useState(null);
  const [loading, setLoading] = React.useState(true);
  const [selected, setSelected] = React.useState(null);
  const [reason, setReason] = React.useState('');
  const [submitting, setSubmitting] = React.useState(false);

  const load = React.useCallback(async () => {
    if (!agencyId) return;
    try {
      const res = await agencyAPI.getCommunicationPolicy(agencyId);
      setPolicyState(res.data);
      setSelected(res.data?.policy || null);
    } catch (e) {
      console.error('Failed to load communication policy:', e);
    } finally {
      setLoading(false);
    }
  }, [agencyId]);

  React.useEffect(() => {
    load();
  }, [load]);

  const canSubmit = (canManage || canRequest) && selected && selected !== policyState?.policy;

  const handleSubmit = async () => {
    if (!canSubmit) return;
    setSubmitting(true);
    try {
      const payload = { policy: selected, reason: reason.trim() || null };
      if (canManage) {
        await agencyAPI.updateCommunicationPolicy(agencyId, payload);
        toast.success('Communication policy updated');
      } else {
        if (!reason.trim()) {
          toast.error('Please provide a reason for the change request');
          setSubmitting(false);
          return;
        }
        await agencyAPI.requestCommunicationPolicyChange(agencyId, payload);
        toast.success('Policy change requested — pending KR review');
      }
      setReason('');
      await load();
    } catch (err) {
      toast.error(getErrorMessage(err, 'Failed to update communication policy'));
    } finally {
      setSubmitting(false);
    }
  };

  const currentPolicy = policyState?.policy || 'direct_contact_allowed';
  const currentLabel = POLICY_LABELS[currentPolicy] || currentPolicy;
  const isDefault = policyState?.status === 'default';
  const pending = policyState?.pending_change;

  return (
    <section
      className="rounded-lg border border-slate-200 bg-slate-50/40 p-4 space-y-3"
      data-testid="agency-communication-policy-section"
    >
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <div className="flex items-center gap-2 flex-wrap">
            <Label className="text-sm font-semibold">Communication Policy</Label>
            <Badge
              className={`text-xs border ${POLICY_TONE[currentPolicy] || 'bg-slate-100 text-slate-700 border-slate-200'}`}
              data-testid="agency-communication-policy-current-badge"
            >
              {currentLabel}
            </Badge>
            {isDefault && (
              <span className="text-[10px] uppercase tracking-wider text-slate-400">
                Platform default
              </span>
            )}
          </div>
          <p className="text-xs text-slate-500 mt-1">
            {loading
              ? 'Loading current policy…'
              : isDefault
                ? 'No explicit policy set — the platform default applies.'
                : `Set by ${policyState?.updated_by || 'unknown'} · ${policyState?.updated_at ? formatDate(policyState.updated_at) : ''}`}
          </p>
        </div>
      </div>

      {pending && (
        <div
          className="rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-800"
          data-testid="agency-communication-policy-pending"
        >
          <span className="font-semibold">Pending change:</span>{' '}
          {POLICY_LABELS[pending.proposed_value] || pending.proposed_value}
          {pending.proposed_by_user_email ? ` — requested by ${pending.proposed_by_user_email}` : ''}
          {pending.proposed_at ? ` on ${formatDate(pending.proposed_at)}` : ''}
          {pending.proposed_reason ? ` · ${pending.proposed_reason}` : ''}
        </div>
      )}

      {(canManage || canRequest) && (
        <>
          <RadioGroup
            value={selected || ''}
            onValueChange={setSelected}
            className="space-y-2"
            data-testid="agency-communication-policy-radio-group"
          >
            {Object.keys(POLICY_LABELS).map((key) => (
              <label
                key={key}
                className="flex items-start gap-2 rounded-md border border-slate-200 bg-white p-2 cursor-pointer hover:border-slate-300 transition-colors"
                data-testid={`agency-communication-policy-option-${key}`}
              >
                <RadioGroupItem value={key} id={`policy-${key}`} className="mt-0.5" />
                <div className="min-w-0">
                  <div className="text-sm font-medium text-slate-900">{POLICY_LABELS[key]}</div>
                  <div className="text-xs text-slate-500 mt-0.5">{POLICY_DESCRIPTIONS[key]}</div>
                </div>
              </label>
            ))}
          </RadioGroup>

          {selected && selected !== currentPolicy && (
            <div className="space-y-1">
              <Label className="text-xs">
                Reason {canRequest && !canManage ? '(required)' : '(optional)'}
              </Label>
              <Textarea
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                rows={2}
                placeholder={
                  canManage
                    ? 'Optional context for the audit log.'
                    : 'Explain why the agency needs this policy change.'
                }
                data-testid="agency-communication-policy-reason"
              />
            </div>
          )}

          <div className="flex justify-end">
            <Button
              type="button"
              size="sm"
              variant={canManage ? 'default' : 'outline'}
              disabled={!canSubmit || submitting}
              onClick={handleSubmit}
              data-testid={
                canManage
                  ? 'agency-communication-policy-save-btn'
                  : 'agency-communication-policy-request-btn'
              }
            >
              {submitting
                ? 'Submitting…'
                : canManage
                  ? 'Save Policy'
                  : 'Request Change'}
            </Button>
          </div>
        </>
      )}
    </section>
  );
};

/**
 * EditAgencyDialog — edits agency identity fields. Owns its own form state
 * and API call; parent supplies the current agency + refresh callback.
 * Phase 20.8.2 adds a Communication Policy section for KR admins (immediate
 * update) and Agency Principals (change request).
 */
export const EditAgencyDialog = ({ open, onOpenChange, agency, onSaved }) => {
  const [form, setForm] = React.useState({});
  const [saving, setSaving] = React.useState(false);

  React.useEffect(() => {
    if (open && agency) setForm(agency);
  }, [open, agency]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await agencyAPI.update(agency.agency_id, {
        agency_name: form.agency_name,
        primary_contact_name: form.primary_contact_name,
        email: form.email,
        phone: form.phone,
        notes_internal: form.notes_internal,
        status: form.status,
        npn: form.npn ?? '',
        ein: form.ein ?? '',
        principal_agent_npn: form.principal_agent_npn ?? '',
        address_line1: form.address_line1 ?? '',
        address_line2: form.address_line2 ?? '',
        city: form.city ?? '',
        state: form.state ?? '',
        zip: form.zip ?? '',
      });
      toast.success('Agency updated successfully');
      onOpenChange(false);
      onSaved?.();
    } catch (err) {
      toast.error(getErrorMessage(err, 'Failed to update agency'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto" data-testid="edit-agency-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading">Edit Agency</DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit}>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Agency Name *</Label>
              <Input
                value={form.agency_name || ''}
                onChange={(e) => setForm((p) => ({ ...p, agency_name: e.target.value }))}
                required
                data-testid="edit-agency-name"
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Contact Name</Label>
                <Input
                  value={form.primary_contact_name || ''}
                  onChange={(e) => setForm((p) => ({ ...p, primary_contact_name: e.target.value }))}
                  data-testid="edit-agency-contact"
                />
              </div>
              <div className="space-y-2">
                <Label>Phone</Label>
                <Input
                  value={formatPhoneInput(form.phone || '')}
                  onChange={(e) => setForm((p) => ({ ...p, phone: e.target.value }))}
                  data-testid="edit-agency-phone"
                />
              </div>
            </div>
            <div className="space-y-2">
              <Label>Email</Label>
              <Input
                type="email"
                value={form.email || ''}
                onChange={(e) => setForm((p) => ({ ...p, email: e.target.value }))}
                data-testid="edit-agency-email"
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Agency NPN</Label>
                <Input
                  value={form.npn || ''}
                  onChange={(e) => setForm((p) => ({ ...p, npn: e.target.value }))}
                  placeholder="Agency NPN"
                  data-testid="edit-agency-npn"
                />
              </div>
              <div className="space-y-2">
                <Label>EIN</Label>
                <Input
                  value={form.ein || ''}
                  onChange={(e) => setForm((p) => ({ ...p, ein: e.target.value }))}
                  placeholder="XX-XXXXXXX"
                  data-testid="edit-agency-ein"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label>Principal (linked agent)</Label>
              <PrincipalPicker
                npn={form.principal_agent_npn}
                name={form.principal_agent_name}
                onPick={(npn, name) => setForm((p) => ({ ...p, principal_agent_npn: npn, principal_agent_name: name }))}
                onClear={() => setForm((p) => ({ ...p, principal_agent_npn: '', principal_agent_name: '' }))}
              />
              <p className="text-[11px] text-slate-400">The Principal is an existing agent. The "Update Agency Info" link is emailed to this person.</p>
            </div>

            <div className="space-y-2">
              <Label>Street Address</Label>
              <Input
                value={form.address_line1 || ''}
                onChange={(e) => setForm((p) => ({ ...p, address_line1: e.target.value }))}
                placeholder="123 Main St"
                data-testid="edit-agency-address1"
              />
            </div>
            <div className="space-y-2">
              <Label>Apt / Suite (optional)</Label>
              <Input
                value={form.address_line2 || ''}
                onChange={(e) => setForm((p) => ({ ...p, address_line2: e.target.value }))}
                data-testid="edit-agency-address2"
              />
            </div>
            <div className="grid grid-cols-3 gap-4">
              <div className="space-y-2">
                <Label>City</Label>
                <Input
                  value={form.city || ''}
                  onChange={(e) => setForm((p) => ({ ...p, city: e.target.value }))}
                  data-testid="edit-agency-city"
                />
              </div>
              <div className="space-y-2">
                <Label>State</Label>
                <Select value={form.state || ''} onValueChange={(v) => setForm((p) => ({ ...p, state: v }))}>
                  <SelectTrigger data-testid="edit-agency-state"><SelectValue placeholder="—" /></SelectTrigger>
                  <SelectContent>
                    {US_STATES.map((s) => <SelectItem key={s.value} value={s.value}>{s.value}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>ZIP</Label>
                <Input
                  value={form.zip || ''}
                  onChange={(e) => setForm((p) => ({ ...p, zip: e.target.value }))}
                  data-testid="edit-agency-zip"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label>Status</Label>
              <Select
                value={form.status || 'active'}
                onValueChange={(v) => setForm((p) => ({ ...p, status: v }))}
              >
                <SelectTrigger data-testid="edit-agency-status"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="active">Active</SelectItem>
                  <SelectItem value="inactive">Inactive</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Internal Notes</Label>
              <Textarea
                value={form.notes_internal || ''}
                onChange={(e) => setForm((p) => ({ ...p, notes_internal: e.target.value }))}
                rows={3}
                data-testid="edit-agency-notes"
              />
            </div>

            {agency?.agency_id && <CommunicationPolicySection agencyId={agency.agency_id} />}
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)} disabled={saving}>Cancel</Button>
            <Button type="submit" disabled={saving} data-testid="save-agency-btn">
              {saving ? 'Saving…' : 'Save Changes'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default EditAgencyDialog;
